"use client";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";

const PARTICLES = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  x: `${Math.random() * 100}%`,
  y: `${Math.random() * 100}%`,
  size: Math.random() * 2.5 + 0.5,
  dur: Math.random() * 10 + 7,
  delay: Math.random() * 5,
}));

function GlitchText({ text }) {
  const [glitching, setGlitching] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlitching(true);
      setTimeout(() => setGlitching(false), 200);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative inline-block select-none" aria-label={text}>
      {/* Main text */}
      <span
        className="relative z-10 block font-black leading-none tracking-tight"
        style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: "clamp(7rem, 22vw, 18rem)",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          WebkitTextStroke: "1px rgba(0,240,255,0.2)",
        }}
      >
        {text}
      </span>

      {/* Glitch layer 1 — cyan */}
      <motion.span
        className="absolute inset-0 block font-black leading-none tracking-tight pointer-events-none"
        style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: "clamp(7rem, 22vw, 18rem)",
          WebkitTextStroke: "1px rgba(0,240,255,0.5)",
          color: "transparent",
          top: 0,
          left: 0,
        }}
        animate={
          glitching
            ? {
                x: [-4, 4, -2, 2, 0],
                opacity: [0, 1, 0.8, 1, 0],
                clipPath: [
                  "inset(20% 0 60% 0)",
                  "inset(40% 0 30% 0)",
                  "inset(60% 0 10% 0)",
                  "inset(10% 0 70% 0)",
                  "inset(0% 0 100% 0)",
                ],
              }
            : { x: 0, opacity: 0 }
        }
        transition={{ duration: 0.18, ease: "linear" }}
      >
        {text}
      </motion.span>

      {/* Glitch layer 2 — blue */}
      <motion.span
        className="absolute inset-0 block font-black leading-none tracking-tight pointer-events-none"
        style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: "clamp(7rem, 22vw, 18rem)",
          WebkitTextStroke: "1px rgba(0,71,255,0.6)",
          color: "transparent",
          top: 0,
          left: 0,
        }}
        animate={
          glitching
            ? {
                x: [3, -3, 2, -2, 0],
                opacity: [0, 0.9, 0.6, 0.9, 0],
                clipPath: [
                  "inset(50% 0 20% 0)",
                  "inset(10% 0 50% 0)",
                  "inset(70% 0 5% 0)",
                  "inset(30% 0 40% 0)",
                  "inset(0% 0 100% 0)",
                ],
              }
            : { x: 0, opacity: 0 }
        }
        transition={{ duration: 0.18, ease: "linear", delay: 0.04 }}
      >
        {text}
      </motion.span>
    </div>
  );
}

/* ─────────────────────────────────────────
   NOT FOUND PAGE
───────────────────────────────────────── */
export default function NotFound() {
  const sectionRef = useRef(null);
  const rawX = useMotionValue(0.5);
  const rawY = useMotionValue(0.5);
  const orbX = useSpring(useTransform(rawX, [0, 1], [-40, 40]), {
    stiffness: 50,
    damping: 20,
  });
  const orbY = useSpring(useTransform(rawY, [0, 1], [-25, 25]), {
    stiffness: 50,
    damping: 20,
  });

  const onMouseMove = (e) => {
    const r = sectionRef.current?.getBoundingClientRect();
    if (!r) return;
    rawX.set((e.clientX - r.left) / r.width);
    rawY.set((e.clientY - r.top) / r.height);
  };

  const fadeUp = (delay = 0) => ({
    initial: { y: 36, opacity: 0 },
    animate: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] },
    },
  });

  return (
    <div
      ref={sectionRef}
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: "#05050b" }}
    >
      {/* ── Grid ── */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.03]"
        aria-hidden
      >
        <defs>
          <pattern
            id="nf-grid"
            width="72"
            height="72"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 72 0 L 0 0 0 72"
              fill="none"
              stroke="white"
              strokeWidth="0.4"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#nf-grid)" />
      </svg>

      {/* ── Particles ── */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        aria-hidden
      >
        {PARTICLES.map((p) => (
          <motion.div
            key={p.id}
            className="absolute rounded-full bg-[#00f0ff]"
            style={{ left: p.x, top: p.y, width: p.size, height: p.size }}
            animate={{ y: [0, -60, 0], opacity: [0, 0.5, 0] }}
            transition={{ duration: p.dur, delay: p.delay, repeat: Infinity }}
          />
        ))}
      </div>

      {/* ── Parallax orbs ── */}
      <motion.div
        style={{ x: orbX, y: orbY }}
        className="absolute inset-0 pointer-events-none"
        aria-hidden
      >
        <div
          className="absolute top-0 left-0 w-[55vw] h-[55vw] max-w-[600px] max-h-[600px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(0,71,255,0.18) 0%, transparent 72%)",
            filter: "blur(80px)",
          }}
        />
        <div
          className="absolute bottom-0 right-0 w-[45vw] h-[45vw] max-w-[520px] max-h-[520px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(0,240,255,0.12) 0%, transparent 72%)",
            filter: "blur(90px)",
          }}
        />
      </motion.div>

      {/* ── Top edge beam ── */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-0 left-0 right-0 h-px origin-left pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, #0047ff, #00f0ff, transparent)",
        }}
      />

      {/* ── Content ── */}
      <div className="relative z-10 flex flex-col items-center text-center px-5 max-w-7xl w-full md:mt-20">
        {/* Section label */}
        <motion.div {...fadeUp(0)} className="flex items-center gap-3">
          <div className="h-px w-10 bg-gradient-to-r from-[#0047ff] to-[#00f0ff]" />
          <span
            className="text-[10px] tracking-[0.32em] uppercase text-[#00f0ff]/55"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            Error — Page not found
          </span>
          <div className="h-px w-10 bg-gradient-to-l from-[#0047ff] to-[#00f0ff]" />
        </motion.div>

        {/* Giant glitching 404 */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mb-2"
        >
          <GlitchText text="404" />
        </motion.div>

        {/* Gradient rule */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="h-px w-40 mb-8 origin-center"
          style={{
            background:
              "linear-gradient(90deg, transparent, #0047ff, #00f0ff, transparent)",
          }}
        />

        {/* Headline */}
        <motion.h1
          {...fadeUp(0.25)}
          className="text-3xl md:text-5xl font-black text-white leading-tight mb-4 tracking-tight"
          style={{ fontFamily: "'Syne', sans-serif" }}
        >
          Signal Lost.
        </motion.h1>

        {/* Sub-text */}
        <motion.p
          {...fadeUp(0.35)}
          className="text-sm md:text-base text-white/35 leading-relaxed mb-10 max-w-md"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          The page you're looking for has been moved, deleted, or never existed.
          Let's get you back on track.
        </motion.p>

        {/* Error code badge */}
        <motion.div
          {...fadeUp(0.42)}
          className="flex items-center gap-2.5 px-4 py-2 rounded-full mb-10
            border border-red-500/20 bg-red-500/[0.04]"
        >
          <motion.span
            className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
          <span
            className="text-[10px] tracking-[0.22em] uppercase text-red-400/70"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            HTTP 404 — Resource not found
          </span>
        </motion.div>

        {/* CTA buttons */}
        <motion.div
          {...fadeUp(0.5)}
          className="flex flex-col sm:flex-row items-center gap-4 mb-0 lg:mb-20"
        >
          {/* Primary — go home */}
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.96 }}>
            <Link
              href="/"
              className="relative group inline-flex items-center gap-3
                px-8 py-3.5 rounded-full font-semibold text-sm tracking-[0.18em] uppercase
                text-[#05050b] overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #00f0ff, #0047ff)",
                boxShadow: "0 0 30px rgba(0,240,255,0.22)",
                fontFamily: "'DM Mono', monospace",
              }}
            >
              <span className="relative z-10">← Back to Home</span>
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.2), transparent)",
                }}
              />
            </Link>
          </motion.div>

          {/* Secondary — contact */}
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.96 }}>
            <Link
              href="/#contact"
              className="inline-flex items-center gap-3
                px-8 py-3.5 rounded-full font-semibold text-sm tracking-[0.18em] uppercase
                text-white/50 border border-white/[0.08]
                hover:border-[#00f0ff]/30 hover:text-[#00f0ff] hover:bg-[#00f0ff]/[0.05]
                transition-all duration-300 backdrop-blur-sm"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              Get in Touch ↗
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* ── Bottom stamp ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <motion.div
          className="w-px h-8 bg-gradient-to-b from-[#00f0ff] to-transparent"
          animate={{ scaleY: [0, 1, 0], opacity: [0, 1, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          style={{ transformOrigin: "top" }}
        />
        <span
          className="text-[9px] tracking-[0.28em] uppercase text-white/18"
          style={{ fontFamily: "'DM Mono', monospace" }}
        >
          Abu Bokor — Portfolio
        </span>
      </motion.div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@800;900&family=DM+Mono:wght@400&family=DM+Sans:wght@300;400&display=swap');
      `}</style>
    </div>
  );
}
